/**
 * One frame of a recording, as the board logs it, and how it is painted into
 * the video after the coach stops.
 *
 * The board logs the scene it is drawing — the same object it hands the
 * renderer — and where the coach's pointer is, in field coordinates. Replayed
 * through the same renderer at the video's size, the frame shows what the
 * coach saw, pointer included, however large their window was.
 */

import type { Vec2 } from '../core/index.ts';
import { render, toScreen, type Scene, type Viewport } from '../renderer/index.ts';
import { pointerFor } from './pointers.ts';
import type { Tool } from './useBoard.ts';

export interface BoardFrame {
  readonly scene: Scene;
  /** The coach's pointer, when it is over the board (a mouse) or touching it (a finger or pen). */
  readonly pointer?: {
    readonly at: Vec2;
    readonly tool: Tool;
    /** The live board's pixels per field unit, so the pointer keeps its size relative to the board. */
    readonly unit: number;
  };
}

/**
 * Where the pointer goes in the video, and how big: at its field position in
 * the video's viewport, scaled with the board so it covers the same share of
 * the pool as it did on screen.
 */
export function placePointer(
  pointer: NonNullable<BoardFrame['pointer']>,
  vp: Viewport,
  size: number,
): { x: number; y: number; size: number } {
  const scale = pointer.unit > 0 ? vp.unit / pointer.unit : 1;
  const at = toScreen(pointer.at, vp);
  return { x: at.x, y: at.y, size: size * scale };
}

/** Paints a logged frame into the video's canvas. */
export function drawBoardFrame(ctx: CanvasRenderingContext2D, width: number, height: number, frame: BoardFrame): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const vp = render(ctx, frame.scene, width, height);
  if (!frame.pointer) return;
  const look = pointerFor(frame.pointer.tool);
  if (!look.image?.complete) return;
  const placed = placePointer(frame.pointer, vp, look.size);
  const scale = placed.size / look.size;
  ctx.drawImage(look.image, placed.x - look.hotspot.x * scale, placed.y - look.hotspot.y * scale, placed.size, placed.size);
}
